import { useState, useEffect, useRef } from "react";
import { reviewPromptService } from "../services/review-prompt.service";
import { CheckReviewEligibility } from "../types/review-prompt";
import { toast } from "react-hot-toast";
import { claimAutoPrompt, useAutoPromptRelease } from "@/lib/autoPrompt";

const PROMPT_ID = "shopify-review";
const CACHE_KEY = "shopify_review_eligibility";
const CACHE_TIMESTAMP_KEY = "shopify_review_eligibility_timestamp";
const CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export function useShopifyReviewPrompt() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eligibility, setEligibility] = useState<CheckReviewEligibility | null>(
    null
  );
  const [isChecking, setIsChecking] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useAutoPromptRelease(PROMPT_ID, isModalOpen);

  useEffect(() => {
    checkEligibility();
    // The layout remounts on every navigation; a timer outliving it would
    // mark the prompt shown without anyone seeing it.
    return () => clearTimeout(showTimerRef.current);
  }, []);

  const getCachedEligibility = (): CheckReviewEligibility | null => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (!cachedData || !cachedTimestamp) {
        return null;
      }

      const timestamp = parseInt(cachedTimestamp, 10);
      const now = Date.now();

      // Check if cache is still valid (less than 7 days old)
      if (now - timestamp < CACHE_DURATION_MS) {
        return JSON.parse(cachedData) as CheckReviewEligibility;
      }

      // Cache expired, clear it
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      return null;
    } catch (error) {
      console.error("Error reading cached eligibility:", error);
      return null;
    }
  };

  const setCachedEligibility = (data: CheckReviewEligibility) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error("Error caching eligibility:", error);
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  };

  const showReviewModal = (_result: CheckReviewEligibility, delay: number) => {
    clearTimeout(showTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      // Something else is on screen: skip without marking it shown, so the
      // next page asks again.
      if (!claimAutoPrompt(PROMPT_ID)) return;
      setIsModalOpen(true);
      // Mark as shown in the backend and update cache
      reviewPromptService
        .updateReviewPromptStatus({ action: "shown" })
        .then(() => {
          // Update cache to mark as shown (prevents showing again until cache expires)
          const updatedCache: CheckReviewEligibility = {
            isEligible: false,
            reason: "recently_shown",
          };
          setCachedEligibility(updatedCache);
        });
    }, delay);
  };

  const checkEligibility = async () => {
    if (isChecking) return;

    // For JivoChat-triggered reviews, always check the backend (bypass cache)
    // This ensures we catch chat_finished events promptly
    const cached = getCachedEligibility();

    // No cache or expired, fetch from backend
    console.log("Fetching review eligibility from backend");
    setIsChecking(true);
    try {
      const result = await reviewPromptService.checkReviewEligibility();
      setEligibility(result);

      // Show modal if eligible
      if (result.isEligible) {
        // Check if this is a JivoChat-triggered review
        const isJivochatTrigger = result.triggerSource === "jivochat";

        // For JivoChat triggers, show immediately (user just finished a chat)
        // For milestone triggers, add a delay for better UX
        const delay = isJivochatTrigger ? 500 : 3000;

        console.log(
          `Showing review prompt (trigger: ${
            result.triggerSource || "milestone"
          }, delay: ${delay}ms)`
        );

        showReviewModal(result, delay);

        // Cache the result after showing
        setCachedEligibility({
          isEligible: false,
          reason: "recently_shown",
        });
      } else {
        // Cache non-eligible result
        setCachedEligibility(result);
      }
    } catch (error) {
      console.error("Error checking review eligibility:", error);

      // On error, try to use cached data if available
      if (cached && cached.isEligible && !cached.reason) {
        setEligibility(cached);
        showReviewModal(cached, 3000);
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleReview = async () => {
    try {
      await reviewPromptService.updateReviewPromptStatus({
        action: "completed",
      });
      // Clear cache so next check gets fresh status
      clearCache();
      setIsModalOpen(false);
      toast.success("Thank you for your review! 🙏");
    } catch (error) {
      console.error("Error updating review status:", error);
      setIsModalOpen(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await reviewPromptService.updateReviewPromptStatus({
        action: "dismissed",
      });
      // Clear cache so next check gets fresh status
      clearCache();
      setIsModalOpen(false);
      toast.success("We appreciate your feedback!");
    } catch (error) {
      console.error("Error dismissing review prompt:", error);
      setIsModalOpen(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Don't clear cache on close - user might see it again next week
  };

  return {
    isModalOpen,
    eligibility,
    handleReview,
    handleDismiss,
    handleClose,
    checkEligibility,
  };
}
